/**
 * The dashboard: what this department has, and what needs you.
 *
 * The budget card reads as **"2026 budget"**, not "available today", and that wording is load
 * bearing. Caps are measured at the close of the budget year (D-05/D-53), so the figure is
 * stable from January — a manager who reads it as "what I have right now" will think it is
 * wrong every time someone's service crosses a tier.
 *
 * Empty states teach the policy. 2026 is the first year HR035 runs at all, so a manager's first
 * visit is the only chance to explain the tiers, the 50% guideline and the 14-day claim window
 * before they meet them as refusals.
 */
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSession } from '@/hooks/session';
import {
  Deadline,
  Money,
  PageHeader,
  ProposalStatusBadge,
  RefLink,
  RosterAge,
} from '@/components/bits';
import { Alert, Badge, Card, EmptyState, LinkButton, Table, Td, Th } from '@/components/ui';
import { activityLabel } from '@/lib/policy';
import { countdownTo, formatDate } from '@/lib/dates';
import type { DepartmentPosition } from '@/lib/types';

export function Dashboard() {
  const session = useSession();
  const year = session.year;

  const positions = useQuery({
    queryKey: ['budget-departments', year],
    queryFn: () => api.departmentPositions(year),
  });
  const freshness = useQuery({ queryKey: ['freshness'], queryFn: api.freshness });

  const mine = useQuery({
    queryKey: ['proposals', 'mine', year],
    queryFn: () => api.proposals({ budget_year: year, limit: 100 }),
  });

  const departments = positions.data?.departments ?? [];
  const proposals = mine.data?.items ?? [];

  const drafts = proposals.filter((item) => item.status === 'draft' || item.status === 'returned');
  const questions = proposals.filter((item) => item.status === 'clarification_requested');
  const awaitingClaim = proposals
    .filter(
      (item) =>
        item.status === 'confirmed' ||
        item.status === 'claim_due' ||
        item.status === 'claim_overdue',
    )
    .sort((a, b) => (a.claim_deadline ?? '').localeCompare(b.claim_deadline ?? ''));

  return (
    <>
      <PageHeader
        title={`${year} bonding budget`}
        subtitle={
          session.me.assignments.departments.length > 0
            ? session.me.assignments.departments.map((department) => department.name).join(', ')
            : session.isHr
              ? 'All departments'
              : 'You are not assigned to a department yet.'
        }
        actions={
          session.me.capabilities.raise_proposal ? (
            <LinkButton to="/proposals/new" variant="primary">
              Propose an event
            </LinkButton>
          ) : null
        }
      />

      {freshness.data ? (
        <div className="mb-6">
          <RosterAge freshness={freshness.data} />
        </div>
      ) : null}

      {questions.length > 0 || drafts.length > 0 ? (
        <Card title="Needs you" className="mb-6">
          <ul className="grid gap-2 text-sm">
            {questions.map((item) => (
              <li key={item.id} className="flex flex-wrap items-center gap-2">
                <Badge tone="warn">Question</Badge>
                <RefLink to={`/proposals/${item.id}`} refNo={item.ref_no} />
                <span className="text-muted-foreground">
                  {item.activity_title} — someone is waiting on an answer
                </span>
              </li>
            ))}
            {drafts.map((item) => (
              <li key={item.id} className="flex flex-wrap items-center gap-2">
                <ProposalStatusBadge status={item.status} />
                <RefLink to={`/proposals/${item.id}`} refNo={item.ref_no} />
                <span className="text-muted-foreground">{item.activity_title}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {/*
        Cards for a manager's own two or three departments; a table once there are more.
        Twenty-three cards — what HR actually sees against the real roster — is not a summary,
        it is a wall, and the company total that HR needs is nowhere on it.
      */}
      <div className="mb-6">
        {positions.isPending ? (
          <p className="text-muted-foreground text-sm">Loading the budget…</p>
        ) : departments.length === 0 ? (
          <FirstVisit />
        ) : departments.length <= 3 ? (
          <div className="grid gap-4 lg:grid-cols-2">
            {departments.map((department) => (
              <BudgetCard key={department.department_id} position={department} year={year} />
            ))}
          </div>
        ) : (
          <AllDepartments positions={departments} year={year} />
        )}
      </div>

      <Card title="Confirmed events waiting on a claim">
        {awaitingClaim.length === 0 ? (
          <EmptyState title="Nothing outstanding">
            When HR confirms an event, it appears here until the claim is filed — within 14 days of
            the event (R-24).
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Reference</Th>
                <Th>Event</Th>
                <Th>Date</Th>
                <Th numeric>Approved</Th>
                <Th>Claim by</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {awaitingClaim.map((item) => (
                <tr key={item.id}>
                  <Td>
                    <RefLink to={`/proposals/${item.id}`} refNo={item.ref_no} />
                  </Td>
                  <Td>
                    {item.activity_title}
                    <p className="text-muted-foreground text-xs">
                      {activityLabel(item.activity_type)}
                    </p>
                  </Td>
                  <Td>{formatDate(item.proposed_date)}</Td>
                  <Td numeric>
                    <Money cents={item.approved_amount_cents} />
                  </Td>
                  <Td>
                    <Deadline date={item.claim_deadline} />
                  </Td>
                  <Td>
                    <ProposalStatusBadge status={item.status} />
                  </Td>
                  <Td>
                    <Link
                      to={`/claims/new?proposal=${item.id}`}
                      className="text-sm underline underline-offset-4"
                    >
                      Raise claim
                    </Link>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

function BudgetCard({ position, year }: { position: DepartmentPosition; year: number }) {
  // R-06: whatever is unspent at the close of the year is forfeited, not carried.
  const closing = countdownTo(`${year}-12-31`);
  const forfeiting = position.available_cents > 0 && closing.days <= 60 && closing.days >= 0;

  return (
    <Card
      title={position.department_name}
      action={
        <span className="text-muted-foreground text-xs">{position.active_headcount} active</span>
      }
    >
      <div className="grid gap-3">
        <div className="flex items-baseline justify-between">
          <span className="text-muted-foreground text-sm">{year} entitlement</span>
          <span className="text-lg font-semibold">
            <Money cents={position.entitlement_cents} />
          </span>
        </div>
        <dl className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <dt className="text-muted-foreground text-xs">Reserved</dt>
            <dd>
              <Money cents={position.held_cents} />
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Spent</dt>
            <dd>
              <Money cents={position.used_cents} />
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Available</dt>
            <dd className="font-medium">
              <Money cents={position.available_cents} />
            </dd>
          </div>
        </dl>

        {position.ineligible > 0 || position.serving_notice > 0 ? (
          <p className="text-muted-foreground text-xs">
            {[
              position.ineligible > 0
                ? `${position.ineligible} under three months' service (R-04)`
                : null,
              position.serving_notice > 0
                ? `${position.serving_notice} serving notice (R-08)`
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}{' '}
            — they may attend, but bring no entitlement.
          </p>
        ) : null}

        {forfeiting ? (
          <Alert tone="warn" title={`The year closes ${closing.label}`}>
            <Money cents={position.available_cents} /> is still unspent. Whatever is unused at the
            close of {year} is forfeited, not carried into {year + 1} (R-06).
          </Alert>
        ) : null}

        <p className="text-muted-foreground text-xs">
          This is the department&rsquo;s whole-year figure, summed over its EMS members wherever
          their entitlement was spent (D-46) — not a running balance for today.
        </p>
      </div>
    </Card>
  );
}

/** The first visit. For most of this company it is also the first year HR035 exists. */
function FirstVisit() {
  return (
    <div className="lg:col-span-2">
      <Card title="How the bonding budget works">
        <div className="grid gap-3 text-sm">
          <p className="text-muted-foreground">
            Each employee brings an entitlement that depends on their length of service at the close
            of the year, and on whether they are Staff or a Worker. Three months of service is the
            minimum (R-04). Someone serving notice may still come, but brings nothing (R-08).
          </p>
          <p className="text-muted-foreground">
            A department&rsquo;s budget for an event is simply the sum of what the people attending
            bring. So there is no per-department allowance to look up: pick who is coming and the
            figure follows.
          </p>
          <p className="text-muted-foreground">
            Aiming for <strong>half the department</strong> is the guideline (R-13). It is a
            guideline, not a rule — several departments here have well over a hundred people, and a
            genuine team outing will fall short every time. Submit it with a reason; HR records the
            exception and confirms.
          </p>
          <p className="text-muted-foreground">
            After the event you have <strong>14 days</strong> to file the claim with receipts and a
            group photo (R-24). Miss it and the reservation stays locked until HR closes it, which
            is a conversation nobody wants to have.
          </p>
          <div className="pt-1">
            <LinkButton to="/proposals/new" variant="primary">
              Propose your first event
            </LinkButton>
          </div>
        </div>
      </Card>
    </div>
  );
}

/**
 * Every department on one screen, for HR.
 *
 * The total matters as much as the rows: S$65,015 for 2026 is the figure HR reconciles with,
 * and a page of cards makes it something you have to add up by hand.
 */
function AllDepartments({
  positions,
  year,
}: {
  positions: readonly DepartmentPosition[];
  year: number;
}) {
  const total = (pick: (position: DepartmentPosition) => number): number =>
    positions.reduce((sum, position) => sum + pick(position), 0);

  return (
    <Card
      title={`All departments — ${year}`}
      action={
        <span className="text-muted-foreground text-xs">
          {positions.length} bonding departments
        </span>
      }
    >
      <Table>
        <thead>
          <tr>
            <Th>Department</Th>
            <Th numeric>Active</Th>
            <Th numeric>Entitlement</Th>
            <Th numeric>Brings nothing</Th>
            <Th numeric>Reserved</Th>
            <Th numeric>Spent</Th>
            <Th numeric>Available</Th>
            <Th>Bringing nothing</Th>
          </tr>
        </thead>
        <tbody>
          {positions.map((position) => (
            <tr key={position.department_id}>
              <Td>{position.department_name}</Td>
              <Td numeric>{position.active_headcount}</Td>
              <Td numeric>
                <Money cents={position.entitlement_cents} />
              </Td>
              <Td numeric>
                <Money cents={position.not_contributing_cents} muted />
              </Td>
              <Td numeric>
                <Money cents={position.held_cents} muted />
              </Td>
              <Td numeric>
                <Money cents={position.used_cents} muted />
              </Td>
              <Td numeric>
                <Money cents={position.available_cents} />
              </Td>
              <Td className="text-xs">
                {position.ineligible + position.serving_notice === 0
                  ? '—'
                  : [
                      position.ineligible > 0 ? `${position.ineligible} under 3 months` : null,
                      position.serving_notice > 0 ? `${position.serving_notice} on notice` : null,
                    ]
                      .filter(Boolean)
                      .join(', ')}
              </Td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-medium">
            <Td>Total</Td>
            <Td numeric>{total((position) => position.active_headcount)}</Td>
            <Td numeric>
              <Money cents={total((position) => position.entitlement_cents)} />
            </Td>
            <Td numeric>
              <Money cents={total((position) => position.not_contributing_cents)} />
            </Td>
            <Td numeric>
              <Money cents={total((position) => position.held_cents)} />
            </Td>
            <Td numeric>
              <Money cents={total((position) => position.used_cents)} />
            </Td>
            <Td numeric>
              <Money cents={total((position) => position.available_cents)} />
            </Td>
            <Td />
          </tr>
        </tfoot>
      </Table>
      <p className="text-muted-foreground mt-3 text-xs">
        The row adds up: entitlement − brings nothing − reserved − spent = available. Somebody under
        three months or serving notice has a tier cap that counts towards the department&rsquo;s
        entitlement and nothing towards what it can spend (R-04/R-08). Workers with no department
        appear in no row at all — {year} totals reconcile only once they are assigned (D-33).
      </p>
    </Card>
  );
}
